/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/camelcase */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { HotKeys } from 'react-hotkeys';
import {
  reduxForm,
  arrayPush,
  FieldArray,
  formValueSelector,
} from 'redux-form';
import { connect } from 'react-redux';
import zenscroll from 'zenscroll';
import { pick, size, isEmpty } from 'lodash';

import {
  intentType,
  entityType,
  constraintSetType,
  utteranceType,
  featuresType,
} from 'types';
import * as intentsSelectors from 'store/services/intents/selectors';
import * as intentsActions from 'store/services/intents/actions';
import * as entitiesSelectors from 'store/services/entities/selectors';
import * as entitiesActions from 'store/services/entities/actions';
import { getHasMachineLearningEnabled } from 'store/services/project/selectors';
import { updateUtterances } from 'store/services/intentUtterances/actions';
import * as intentUtterancesSelectors from 'store/services/intentUtterances/selectors';
import { getHasSelectedTestSet } from 'store/services/testSets/selectors';
import { getTestSetEntriesListCount } from 'store/services/testSetEntries/selectors';
import { getFeatures } from 'store/services/features/selectors';

import Form from 'components/Form';
import InspectTabs from 'components/InspectTabs';
import InspectButton from 'components/InspectButton';
import InspectFooter from 'components/InspectFooter';
import InspectLoader from 'components/InspectLoader';

import FORM_NAMES from 'constants/formNames';

import Header from './components/Header';
import ConstraintSetsFieldArray from './components/ConstraintSetsFieldArray';
import GeneralTab from './components/GeneralTab';
import MachineLearning from './components/MachineLearning';
import { validate } from './utils';

import './style.scss';

const keyMap = {
  leave: 'esc',
  save: 'alt+s',
};

const isXpackWithNoConstraintSets = intent =>
  intent.xpack_resource && isEmpty(intent.constraint_sets);

export class IntentInspect extends Component {
  keyHandlers = {
    leave: this.props.onClose,
    save: this.submitWithValidation,
  };

  static propTypes = {
    partialExport: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    addField: PropTypes.func,
    allEntities: PropTypes.arrayOf(entityType),
    allIntents: PropTypes.arrayOf(intentType),
    change: PropTypes.func,
    changeTab: PropTypes.func,
    closeTabs: PropTypes.func,
    constraintSets: PropTypes.arrayOf(constraintSetType),
    currentIntent: intentType,
    deleteIntent: PropTypes.func,
    dirty: PropTypes.bool,
    executeAnnotator: PropTypes.func,
    featureTypes: PropTypes.arrayOf(featuresType),
    getAllEntities: PropTypes.func,
    getAllIntents: PropTypes.func,
    getIntent: PropTypes.func,
    handleSubmit: PropTypes.func,
    hasConstraintSetsWithoutConditions: PropTypes.number,
    hasMachineLearningFeatureEnabled: PropTypes.bool,
    hasSelectedTestSet: PropTypes.bool,
    id: PropTypes.string,
    isIntentLoading: PropTypes.bool,
    isLoading: PropTypes.bool,
    isLoadingAllEntities: PropTypes.bool,
    isLoadingAllIntents: PropTypes.bool,
    saveUtterances: PropTypes.func,
    selectedTabIndex: PropTypes.number,
    testSetEntriesCount: PropTypes.number,
    touch: PropTypes.func,
    updateIntent: PropTypes.func,
    utterances: PropTypes.arrayOf(utteranceType),
    utterancesIsLoading: PropTypes.bool,
    valid: PropTypes.bool,
    onItemDeselect: PropTypes.func,
  };

  static defaultProps = {
    constraintSets: [],
    utterancesIsLoading: false,
    isIntentLoading: true,
  };

  componentDidMount = async () => {
    const { id, getIntent } = this.props;

    if (id) {
      await getIntent(id);
      this.focusNameInput();
    }
  };

  componentWillReceiveProps = async ({ id }) => {
    if (!!id && this.props.id !== id) {
      await this.props.getIntent(id);
      this.focusNameInput();
    }
  };

  componentWillUnmount() {
    this.props.onItemDeselect();
  }

  getInspectTabs = () => {
    const hasWarning = !size(this.props.constraintSets);
    let tabs = [
      { label: 'General', tabIndex: 1 },
      { label: 'Constraints', tabIndex: 2 },
    ];

    if (this.props.hasMachineLearningFeatureEnabled) {
      tabs = [
        ...tabs,
        {
          label: 'Machine Learning',
          tabIndex: 3,
        },
      ];
    }

    if (!hasWarning) {
      return tabs;
    }

    return tabs.map(tab =>
      tab.tabIndex === 2
        ? {
            ...tab,
            hasWarning,
          }
        : tab,
    );
  };

  focusNameInput = () => {
    if (this.nameInputRef) {
      this.nameInputRef.focus();
    }
  };

  submitInspect = async formValues => {
    const {
      updateIntent,
      currentIntent,
      hasMachineLearningFeatureEnabled,
      saveUtterances,
    } = this.props;

    const {
      enabled,
      name,
      color,
      description,
      xpackConstraintSetsAreActive,
      utterances,
    } = formValues;

    // remove all_references from payload
    const constraint_sets = formValues.constraint_sets.map(
      ({ all_references, ...keepAttrs }) => keepAttrs,
    );

    const updatedIntent = {
      ...currentIntent,
      enabled,
      name,
      color,
      description,
      override: !xpackConstraintSetsAreActive,
      constraint_sets,
    };

    await updateIntent(updatedIntent.id, updatedIntent);

    if (hasMachineLearningFeatureEnabled) {
      await saveUtterances(utterances);
    }
  };

  handleNameInputRef = el => {
    this.nameInputRef = el;
  };

  submitWithValidation = () =>
    // handleSubmit is a high order function that is passed to this component by redux form.
    // It will run validation, both sync and async, and, if the form is valid, it will call submitInspect
    this.props.handleSubmit(this.submitInspect)();

  handleSaveAndRun = async () => {
    await this.submitWithValidation();
    this.props.executeAnnotator();
  };

  handleDelete = () => {
    this.props.deleteIntent(this.props.currentIntent.id);
    this.props.closeTabs();
  };

  handleAddEntity = () => {
    this.props.addField(FORM_NAMES.INTENTS.INSPECT, 'constraint_sets', {
      autoFocus: true,
    });
  };

  handleOnAddConstraintSet = event => {
    event.stopPropagation();
    event.preventDefault();
    this.props.addField(FORM_NAMES.INTENTS.INSPECT, 'constraint_sets', {
      name: `ConstraintSet-${this.props.constraintSets.length + 1}`,
      conditions: [
        {
          entity: '',
          entity_reference_obj: {},
        },
      ],
      all_references: [],
    });
    this.scrollToBottom(this.fields, '');
  };

  handleContainerRef = ref => {
    this.fields = ref;
    this.zenScroller = zenscroll.createScroller(this.fields, 700, 0);
  };

  scrollToBottom = (height, type) => {
    setTimeout(() => {
      if (type === 'entity') {
        this.zenScroller.to(height);
      } else {
        this.zenScroller.toY(height.scrollHeight);
      }
    }, 100);
  };

  handlePartialExport = () => {
    this.props.partialExport(`intent-${this.props.currentIntent.name}`, [
      this.props.id,
    ]);
  };

  renderGeneralTab = () => {
    const { selectedTabIndex, currentIntent } = this.props;

    if (selectedTabIndex !== 1) {
      return null;
    }

    return (
      <GeneralTab
        handlersOfNameInputRef={[this.handleNameInputRef]}
        intent={currentIntent}
      />
    );
  };

  renderConstraintSetsTab = () => {
    const {
      selectedTabIndex,
      isLoadingAllIntents,
      isLoadingAllEntities,
      hasMachineLearningFeatureEnabled,
      currentIntent: { xpack_resource },
      utterances,
      featureTypes,
      getAllIntents,
      allIntents,
      getAllEntities,
      allEntities,
      touch,
      change,
    } = this.props;
    if (selectedTabIndex !== 2) {
      return null;
    }

    return (
      <FieldArray
        addEntity={this.handleAddEntity}
        allEntities={allEntities}
        allIntents={allIntents}
        change={change}
        component={ConstraintSetsFieldArray}
        featureTypes={featureTypes}
        getAllEntities={getAllEntities}
        getAllIntents={getAllIntents}
        hasMachineLearningFeatureEnabled={hasMachineLearningFeatureEnabled}
        isLoadingAllEntities={isLoadingAllEntities}
        isLoadingAllIntents={isLoadingAllIntents}
        isXpack={xpack_resource}
        name="constraint_sets"
        refElement={this.fields}
        touch={touch}
        utterances={utterances}
        onScroll={this.scrollToBottom}
      />
    );
  };

  renderMachineLearning = () => {
    const {
      selectedTabIndex,
      utterancesIsLoading,
      isIntentLoading,
    } = this.props;

    if (selectedTabIndex !== 3) {
      return null;
    }

    return (
      <div styleName="machine-learning">
        <FieldArray
          component={MachineLearning}
          isLoading={utterancesIsLoading || isIntentLoading}
          name="utterances"
        />
      </div>
    );
  };

  renderTabs() {
    const { changeTab, selectedTabIndex, currentIntent } = this.props;

    return (
      <InspectTabs
        changeTab={changeTab}
        closeTabs={this.props.onClose}
        icon="icon-intents"
        item={currentIntent}
        selectedTabIndex={selectedTabIndex}
        tabs={this.getInspectTabs()}
      />
    );
  }

  renderFooter() {
    const {
      hasSelectedTestSet,
      isLoading,
      hasConstraintSetsWithoutConditions,
      testSetEntriesCount,
      currentIntent: { archived },
      valid,
      dirty,
    } = this.props;

    const disabled = !!(
      isLoading ||
      archived ||
      !dirty ||
      !valid ||
      hasConstraintSetsWithoutConditions
    );

    return (
      <InspectFooter>
        <InspectButton
          isFooter
          data-test="export-intent-button"
          disabled={isXpackWithNoConstraintSets(this.props.currentIntent)}
          onClick={this.handlePartialExport}
        >
          Export
        </InspectButton>

        {hasSelectedTestSet && testSetEntriesCount > 0 && (
          <InspectButton
            isFooter
            data-test="save-and-run-intent-button"
            disabled={disabled}
            onClick={this.handleSaveAndRun}
          >
            Save & Run
          </InspectButton>
        )}
        <InspectButton
          isFooter
          isSave
          data-test="save-intent-button"
          disabled={disabled}
          type="submit"
        >
          Save
        </InspectButton>
      </InspectFooter>
    );
  }

  render() {
    const { selectedTabIndex, isLoading, currentIntent } = this.props;

    return (
      <HotKeys handlers={this.keyHandlers} keyMap={keyMap} styleName="inspect">
        {this.renderTabs()}

        <Header
          currentIntent={currentIntent}
          selectedTabIndex={selectedTabIndex}
          onAddConstraintSet={this.handleOnAddConstraintSet}
          onDelete={this.handleDelete}
        />

        <Form
          styleName="form"
          onSubmit={e => {
            e.preventDefault();
            this.submitWithValidation();
          }}
        >
          <div ref={this.handleContainerRef} styleName="wrapper">
            <InspectLoader isLoading={isLoading} />
            {this.renderGeneralTab()}
            {this.renderConstraintSetsTab()}
            {this.renderMachineLearning()}
            {this.renderFooter()}
          </div>
        </Form>
      </HotKeys>
    );
  }
}

const mapStateToProps = state => {
  const utterances = intentUtterancesSelectors.getUtterances(state);

  const initialValues = {
    ...pick(intentsSelectors.getIntentWithPublicFeaturesMap(state), [
      'name',
      'enabled',
      'description',
      'color',
      'constraint_sets',
      'xpackConstraintSetsAreActive',
    ]),
    utterances,
  };

  const constraintSetsFormValue = formValueSelector(FORM_NAMES.INTENTS.INSPECT)(
    state,
    'constraint_sets',
  );

  return {
    initialValues,
    isLoading: intentsSelectors.getIsUpdating(state),
    isIntentLoading: intentsSelectors.getIntentIsLoading(state),
    utterancesIsLoading: intentUtterancesSelectors.getIsLoading(state),
    utterances,
    currentIntent: intentsSelectors.getIntent(state),
    allIntents: intentsSelectors.getAllIntentsExceptTheCurrent(state),
    isLoadingAllIntents: intentsSelectors.getIsLoadingAll(state),
    isLoadingAllEntities: entitiesSelectors.getIsLoadingAll(state),
    allEntities: entitiesSelectors.getAllEntities(state),
    constraintSets: constraintSetsFormValue,
    hasConstraintSetsWithoutConditions: intentsSelectors.getConstraintSetsWithoutConditions(
      constraintSetsFormValue,
    ),
    hasMachineLearningFeatureEnabled: getHasMachineLearningEnabled(state),
    hasSelectedTestSet: getHasSelectedTestSet(state),
    testSetEntriesCount: getTestSetEntriesListCount(state),
    featureTypes: getFeatures(state),
  };
};

const mapDispatchToProps = {
  partialExport: intentsActions.partialExportSingleIntent,
  getIntent: intentsActions.getIntent,
  deleteIntent: intentsActions.deleteIntent,
  getAllIntents: intentsActions.getAllIntents,
  getAllEntities: entitiesActions.getAllEntities,
  updateIntent: intentsActions.updateIntent,
  checkIntentName: intentsActions.checkIntentName,
  addField: arrayPush,
  saveUtterances: updateUtterances,
};

const asyncValidate = (formProps, _, props) =>
  props.checkIntentName('name', formProps.name, props.currentIntent.id);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    asyncValidate,
    validate,
    asyncChangeFields: ['name'],
    enableReinitialize: true,
    form: FORM_NAMES.INTENTS.INSPECT,
    touchOnChange: true,
  })(IntentInspect),
);
